/**
 * @file RisksTab Component
 * @description Tab for managing negative impacts/risks for an investment.
 * Captures what bad things happen if the investment is NOT made.
 * Examples: Revenue loss, compliance violations, client churn risk.
 */

import {
  useNegativeImpacts,
  useCreateNegativeImpact,
  useUpdateNegativeImpact,
  useDeleteNegativeImpact,
  useActiveNegativeImpactCategories,
} from '@medsuite/api-client';
import {
  Button,
  Input,
  TextArea,
  Card,
  Modal,
  ModalFooter,
  Select,
  useDialog,
} from '@medsuite/ui-components';
import { useState, useEffect, useRef } from 'react';

import type {
  Investment,
  NegativeImpactWithCategory,
  AmountFrequency,
} from '@medsuite/models';

interface RisksTabProps {
  investment: Investment;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
}

interface LocalNegativeImpact {
  id?: number;
  category_id: number;
  category_name?: string;
  title: string;
  description: string | null;
  amount: number | null;
  amount_frequency: AmountFrequency | null;
  clients_at_risk: number | null;
  arr_at_risk: number | null;
}

// Amount frequency options for Select component
const amountFrequencyOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select frequency' },
  { value: 'O', label: 'One-Time' },
  { value: 'M', label: 'Monthly' },
  { value: 'A', label: 'Annually' },
];

// Amount frequency labels for display
const AmountFrequencyLabel: Record<string, string> = {
  O: 'One-Time',
  M: 'Monthly',
  A: 'Annually',
};

/**
 * Format currency helper
 */
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format number helper
 */
const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return new Intl.NumberFormat('en-US').format(value);
};

export function RisksTab({
  investment,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isEditing: _isEditing,
  setIsEditing,
}: RisksTabProps) {
  const { confirm } = useDialog();
  const investmentId = investment?.investment_id ?? 0;
  const isValidId = investmentId > 0 && !isNaN(investmentId);

  // Fetch negative impacts
  const { data: negativeImpacts = [], isLoading } = useNegativeImpacts(investmentId, {
    enabled: isValidId,
  });

  // Fetch active categories for dropdown
  const { data: categories = [] } = useActiveNegativeImpactCategories({
    enabled: isValidId,
  });

  // Mutations
  const createNegativeImpact = useCreateNegativeImpact(investmentId);
  const updateNegativeImpact = useUpdateNegativeImpact(investmentId);
  const deleteNegativeImpact = useDeleteNegativeImpact(investmentId);

  // Local state for editing
  const [localImpacts, setLocalImpacts] = useState<LocalNegativeImpact[]>([]);

  // Track previous investment ID and impacts to prevent infinite loops
  const prevInvestmentIdRef = useRef<number | null>(null);
  const prevImpactsRef = useRef<string>('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<LocalNegativeImpact>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Build category options from fetched data
  const categoryOptions: Array<{ value: string; label: string }> = [
    { value: '', label: 'Select category' },
    ...categories.map((cat) => ({
      value: String(cat.id),
      label: cat.name,
    })),
  ];

  // Sync with fetched data
  useEffect(() => {
    if (negativeImpacts) {
      const impactsJson = JSON.stringify(negativeImpacts);

      // Only update if investment changed or impacts data changed
      if (
        prevInvestmentIdRef.current !== investmentId ||
        prevImpactsRef.current !== impactsJson
      ) {
        const mappedImpacts: LocalNegativeImpact[] = negativeImpacts.map(
          (impact: NegativeImpactWithCategory) => ({
            id: impact.id,
            category_id: impact.category_id,
            category_name: impact.category_name,
            title: impact.title || '',
            description: impact.description,
            amount: impact.amount,
            amount_frequency: impact.amount_frequency,
            clients_at_risk: impact.clients_at_risk,
            arr_at_risk: impact.arr_at_risk,
          })
        );
        setLocalImpacts(mappedImpacts);
        prevInvestmentIdRef.current = investmentId;
        prevImpactsRef.current = impactsJson;
      }
    }
  }, [investmentId, negativeImpacts]);

  // Calculate totals
  const totalClientsAtRisk = localImpacts.reduce(
    (sum, impact) => sum + (impact.clients_at_risk || 0),
    0
  );
  const totalArrAtRisk = localImpacts.reduce(
    (sum, impact) => sum + (impact.arr_at_risk || 0),
    0
  );
  const totalAmount = localImpacts.reduce(
    (sum, impact) => sum + (impact.amount || 0),
    0
  );

  // Handlers
  const handleOpenModal = (index?: number) => {
    if (index !== undefined && index >= 0) {
      // Edit existing
      const impact = localImpacts[index];
      if (!impact) {
        return;
      }

      setEditingIndex(index);
      setForm({
        ...impact,
      });
    } else {
      // Add new
      setEditingIndex(null);
      setForm({
        category_id: 0,
        title: '',
        description: null,
        amount: null,
        amount_frequency: null,
        clients_at_risk: null,
        arr_at_risk: null,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingIndex(null);
    setForm({});
    setIsSaving(false);
  };

  const handleSave = async () => {
    // Guard: Prevent saving with invalid investment ID
    if (!isValidId) {
      console.error('Cannot save negative impact: invalid investment ID');
      return;
    }

    // Validate required fields
    if (!form.category_id || !form.title) {
      return;
    }

    // Validate: amount_frequency required if amount is provided
    if (form.amount && !form.amount_frequency) {
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        category_id: form.category_id,
        title: form.title || '',
        description: form.description || null,
        amount: form.amount != null ? Number(form.amount) : null,
        amount_frequency: form.amount_frequency || null,
        clients_at_risk: form.clients_at_risk != null ? Number(form.clients_at_risk) : null,
        arr_at_risk: form.arr_at_risk != null ? Number(form.arr_at_risk) : null,
      };

      if (editingIndex !== null) {
        // Update existing
        const impact = localImpacts[editingIndex];
        if (impact?.id) {
          await updateNegativeImpact.mutateAsync({
            negativeImpactId: impact.id,
            data: payload,
          });
        }
      } else {
        // Add new
        await createNegativeImpact.mutateAsync(payload);
      }
      setIsEditing(true);
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save negative impact:', error);
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    const impact = localImpacts[index];

    if (!impact) {
      return;
    }

    if (impact.id) {
      const confirmed = await confirm({
        title: 'Delete Negative Impact',
        message: 'Are you sure you want to delete this negative impact? This action cannot be undone.',
        variant: 'danger',
        confirmText: 'Delete',
      });

      if (!confirmed) {
        return;
      }

      try {
        await deleteNegativeImpact.mutateAsync(impact.id);
      } catch (error) {
        console.error('Failed to delete negative impact:', error);
        return;
      }
    }

    setLocalImpacts(localImpacts.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  // Get category name by ID
  const getCategoryName = (categoryId: number): string => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  // Render modal
  const renderModal = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSave();
    };

    const hasAmount = form.amount != null && form.amount > 0;

    return (
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingIndex !== null ? 'Edit Negative Impact' : 'Add Negative Impact'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category *
              </label>
              <Select
                options={categoryOptions}
                value={String(form.category_id || '')}
                onChange={(value) =>
                  setForm({ ...form, category_id: Number(value) || 0 })
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                Type of negative impact if investment is not made
              </p>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title *
              </label>
              <Input
                value={form.title || ''}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Brief description of the risk"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <TextArea
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Detailed explanation of the negative impact"
                rows={3}
              />
            </div>

            {/* Amount Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Financial Impact (Optional)
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amount ($)
                  </label>
                  <Input
                    type="number"
                    value={form.amount || ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        amount: parseFloat(e.target.value) || null,
                      })
                    }
                    placeholder="e.g., 20000"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Frequency {hasAmount && '*'}
                  </label>
                  <Select
                    options={amountFrequencyOptions}
                    value={form.amount_frequency || ''}
                    onChange={(value) =>
                      setForm({
                        ...form,
                        amount_frequency: (value as AmountFrequency) || null,
                      })
                    }
                  />
                  {hasAmount && !form.amount_frequency && (
                    <p className="text-xs text-red-500 mt-1">
                      Frequency is required when amount is provided
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Client Risk Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Client Risk (Optional)
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Clients at Risk
                  </label>
                  <Input
                    type="number"
                    value={form.clients_at_risk || ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        clients_at_risk: parseInt(e.target.value, 10) || null,
                      })
                    }
                    placeholder="Number of clients"
                    min="0"
                    step="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    ARR at Risk ($)
                  </label>
                  <Input
                    type="number"
                    value={form.arr_at_risk || ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        arr_at_risk: parseFloat(e.target.value) || null,
                      })
                    }
                    placeholder="Annual recurring revenue at risk"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>

          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseModal}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                isSaving ||
                !form.category_id ||
                !form.title ||
                (hasAmount && !form.amount_frequency)
              }
            >
              {isSaving
                ? 'Saving...'
                : editingIndex !== null
                  ? 'Save Changes'
                  : 'Add Negative Impact'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  // Guard: Show loading state if investment ID is invalid
  if (!isValidId) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500 dark:text-gray-400">Loading investment data...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500 dark:text-gray-400">Loading negative impacts...</div>
      </div>
    );
  }

  return (
    <>
      {renderModal()}
      <div className="space-y-6 p-6">
        {/* Header Card */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Negative Impacts / Risks
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  What bad things happen if this investment is NOT made?
                </p>
              </div>
              <Button onClick={() => handleOpenModal()} size="sm">
                + Add Negative Impact
              </Button>
            </div>

            {localImpacts.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-gray-400 mb-4">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No negative impacts documented yet.
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
                  Document the risks and consequences of not proceeding with this investment.
                </p>
                <Button onClick={() => handleOpenModal()} variant="outline">
                  Add Your First Negative Impact
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Clients at Risk
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          ARR at Risk
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {localImpacts.map((impact, index) => (
                        <tr key={impact.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-4">
                            <span className="px-2 py-0.5 rounded text-xs font-medium text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30">
                              {impact.category_name || getCategoryName(impact.category_id)}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {impact.title || '—'}
                            </div>
                            {impact.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs truncate">
                                {impact.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-right">
                            {impact.amount ? (
                              <div>
                                <span className="font-mono text-red-600 dark:text-red-400">
                                  {formatCurrency(impact.amount)}
                                </span>
                                {impact.amount_frequency && (
                                  <span className="text-xs text-gray-400 block">
                                    {AmountFrequencyLabel[impact.amount_frequency]}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-right font-mono text-gray-900 dark:text-gray-100">
                            {formatNumber(impact.clients_at_risk)}
                          </td>
                          <td className="px-4 py-4 text-sm text-right font-mono text-red-600 dark:text-red-400">
                            {formatCurrency(impact.arr_at_risk)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm space-x-2">
                            <Button
                              onClick={() => handleOpenModal(index)}
                              variant="ghost"
                              size="sm"
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDelete(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-3">
                    Risk Summary
                  </h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Total Risks
                      </div>
                      <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                        {localImpacts.length}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Total Amount
                      </div>
                      <div className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(totalAmount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Clients at Risk
                      </div>
                      <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                        {formatNumber(totalClientsAtRisk)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        ARR at Risk
                      </div>
                      <div className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(totalArrAtRisk)}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

export default RisksTab;
